import { useQuery, keepPreviousData } from "@tanstack/react-query";

import { apiFetch, isOfflineError } from "@/lib/api-client";
import { db } from "@/lib/idb-schema";
import { filterOwned, readOwnerIdentity } from "@/lib/owner-scope";
import { draftsToEvaluationsList } from "@/lib/drafts-to-evaluations-list";
import type {
  EvaluationsListResponse,
  EvaluationState,
} from "@/hooks/queries/evaluation-types";

// Evaluations list query hook (Story 6.9, consuming the Story 6.10 endpoint).
// Mirrors `useListClients` exactly: `apiFetch` + `useQuery`, a granular query key,
// no `skipAuthRefresh` (a post-session data call that rides the silent-refresh
// path). `apiFetch` already unwraps the `{ data: {...} }` envelope, so the result
// type is the unwrapped `PaginatedData<EvaluationListItem>`.

/** Root key for every evaluations-list query — `invalidateQueries(["evaluations"])` matches all. */
export const EVALUATIONS_QUERY_KEY = "evaluations";

/** Backend defaults (mirror `list_evaluations` query params: page≥1, 1≤limit≤100). */
const DEFAULT_PAGE = 1;
const DEFAULT_LIMIT = 20;

export interface ListEvaluationsParams {
  /** 1-indexed page number (backend default 1). */
  page?: number;
  /** Page size (backend default 20, max 100). */
  limit?: number;
  /**
   * Canonical lifecycle states to include (sent comma-joined as `?status=`).
   * Omitted ⇒ the backend default view (all states EXCEPT `archived`).
   */
  status?: EvaluationState[];
  /** Restrict to one client (UUID). Omitted ⇒ all of the caller's evaluations. */
  clientId?: string;
}

/** Stable, fully-resolved query key so each page/filter caches independently. */
export function evaluationsListQueryKey(params: Required<Omit<ListEvaluationsParams, "status" | "clientId">> & {
  status: EvaluationState[];
  clientId: string | undefined;
}) {
  return [EVALUATIONS_QUERY_KEY, params] as const;
}

/** Build the `/evaluations` query string the same shape `useListClients` uses (manual, so the
 * exact URL is assertable and `status`'s comma list is not percent-mangled). */
function buildPath(page: number, limit: number, status: EvaluationState[], clientId: string | undefined): string {
  let path = `/evaluations?page=${page}&limit=${limit}`;
  if (status.length > 0) path += `&status=${status.join(",")}`;
  if (clientId) path += `&client_id=${clientId}`;
  return path;
}

export function useListEvaluations(params: ListEvaluationsParams = {}) {
  const page = params.page ?? DEFAULT_PAGE;
  const limit = params.limit ?? DEFAULT_LIMIT;
  const status = params.status ?? [];
  const clientId = params.clientId;

  return useQuery({
    queryKey: evaluationsListQueryKey({ page, limit, status, clientId }),
    // gh-471 (Option A) — network-first, IDB-fallback (mirrors `useGetEvaluation`, gh-459). ONLINE:
    // the server list is authoritative. OFFLINE: the network call fails with a transport error, so
    // reconstruct the list from the local `drafts` table — this is what gives a COLD OFFLINE boot a
    // navigable path to the in-progress draft (Workspace in-progress card / evals row →
    // `/evaluations/:id`, which gh-459 resumes from IDB). Only a genuine transport failure
    // (`isOfflineError`) falls back; a real 4xx/5xx rethrows so the error banner shows and stale IDB
    // never masks a revoked/broken list (gh-458 Issue 1 lesson).
    queryFn: async () => {
      try {
        return await apiFetch<EvaluationsListResponse>(buildPath(page, limit, status, clientId));
      } catch (err) {
        if (isOfflineError(err)) {
          // Story 19.6 Unit E — scope the local read to the clinician using THIS device. On a shared
          // device the table can still hold the previous clinician's drafts (they are deliberately
          // never deleted — the `outbound_queue` gates gh-461's offline restore), and without this
          // filter the offline list showed them. Cache hygiene, not authorization: RLS + the endpoint
          // remain the guarantee (rule-06). An unattributed pre-v3 row belongs to nobody and is
          // likewise hidden. A hidden row still yields a RESOLVED `count: 0`, so the screen reaches
          // its real empty state rather than the gh-471 infinite spinner.
          const owner = await readOwnerIdentity();
          const rows = filterOwned(await db.drafts.toArray(), owner);
          return draftsToEvaluationsList(rows, { page, limit, status, clientId });
        }
        throw err;
      }
    },
    // Keep the current page/filter visible while the next loads, so paging or
    // switching status filters doesn't flash the full-screen loading state.
    placeholderData: keepPreviousData,
    // gh-471 — under the default `networkMode: "online"` React Query PAUSES the query while
    // `navigator.onLine` is false and never runs `queryFn`, so the IDB fallback above would be
    // unreachable and the list would hang on "Loading…" forever on a cold offline boot. `"always"`
    // runs `queryFn` regardless of connectivity; `retry: false` settles the offline fallback in one
    // pass (no retry churn). Scoped to THIS query. Same rationale as `useGetEvaluation` (gh-459).
    networkMode: "always",
    retry: false,
  });
}

/**
 * Count of evaluations in a single state — the cheap query behind the KPI tiles
 * (Drafts / Ready to score / Completed; Open evaluations / Ready to score on home).
 * Asks for the smallest possible page (`limit=1`) and reads only `count`, so the
 * tile costs one tiny request. It caches under its own key (the `limit=1` makes it
 * distinct from the full `limit=20` list query — they never share a cache entry).
 * Returns the unwrapped query so the caller maps loading / error (→ "—") itself.
 */
export function useEvaluationStatusCount(status: EvaluationState) {
  return useListEvaluations({ status: [status], limit: 1 });
}
